import { Color, Spacing } from '@persona-id/design-tokens';
import { GridIcon, XIcon } from '@persona-id/design-tokens/icons';
import { compact } from 'lodash/fp';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { MEDIA_WIDTHS, mediaQuery } from 'shared/styles/variables';
import styled from 'styled-components';

import useEnhancedLimitingForAdditional, {
  MAX_ACTIONS_FOR_NON_ENHANCED,
  MAX_CONDITIONAL_STEP_FOR_NON_ENHANCED,
  MAX_STEPS_FOR_NON_ENHANCED,
} from 'dashboard/apps/InquiryTemplateEditor/hooks/useEnhancedLimitingForAdditional';
import useHasAccessForTierDeterminator from 'dashboard/apps/InquiryTemplateEditor/hooks/useHasAccessForTierDeterminator';
import { EditorAccessTier } from 'dashboard/apps/InquiryTemplateEditor/hooks/useIsEditableWithOptions';
import useOnEnhancedLearnMoreClick from 'dashboard/apps/InquiryTemplateEditor/hooks/useOnEnhancedLearnMoreClick';
import InquiriesGatingSticker, {
  EnhancedGatingStickerType,
} from 'dashboard/apps/InquiryTemplateEditor/InquiriesGatingSticker';
import { AddStepConfig } from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplateFlowEditor/types';
import InquiryTemplatePreview from 'dashboard/apps/InquiryTemplateEditor/InquiryTemplatePreview';
import LearnMoreTooltip from 'dashboard/apps/InquiryTemplateEditor/LearnMoreTooltip';
import TemplateModuleCard from 'dashboard/apps/InquiryTemplateEditor/TemplateModuleCard';
import ProductUpsellTooltip from 'dashboard/components/ProductUpsellTooltip';
import { ModalContainer } from 'dashboard/layouts/ModalLayout';
import {
  InquiryTemplateModuleOption,
  ModuleOptionKey,
  ModulePreview,
  ModulePreviewMap,
} from 'dashboard/models/inquiry-template-module';
import { Feature } from 'dashboard/models/inquiry-template-version';
import { ThemeAttributes } from 'dashboard/models/theme';
import { Z_INDICES } from 'dashboard/styles/variables';
import Button from 'design-system/components/Button';
import { Heading, Paragraph } from 'design-system/components/Text';
import Tooltip from 'design-system/components/Tooltip';
import useFeatureFlag from 'lib/hooks/useFeatureFlag';

export const terminalModuleHelpText = 'This module may only be added to the end of a branch';

const StyledModalContainer = styled(ModalContainer)`
  position: relative;
  flex-direction: column;
  height: clamp(600px, 85vh, 1050px);
  width: clamp(980px, 90vw, 1100px);
  padding: 0;
`;

/* modal header components */
const StyledModalHeader = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  border-bottom: 1px solid ${({ theme }) => theme.color.border.secondary};
  height: 72px;
  width: 100%;
  padding: ${Spacing[5]};
  z-index: 1;
`;

const HeaderTitleWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
`;

const SearchFilterRow = styled.div`
  display: grid;
  grid-template-columns: 2fr minmax(300px, 1fr);
  grid-gap: ${Spacing[4]};
  margin-top: ${Spacing[4]};

  @media ${mediaQuery({ max: MEDIA_WIDTHS.desktop })} {
    grid-template-columns: none;
    grid-template-rows: auto auto;
  }
`;

const CloseButton = styled.button.attrs(() => ({ type: 'button' }))`
  cursor: pointer;
  margin-left: auto;
  background: none;
  border: 0;
`;

/* modal content */
const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  width: 100%;
  height: 100%;
  overflow: hidden;
`;

const LeftColumn = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  grid-gap: ${Spacing[3]};
  flex: none;
  height: 100%;
  width: 360px;
  overflow-y: auto;
  padding: ${Spacing[5]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-right: 1px solid ${({ theme }) => theme.color.border.primary};
  border-top-left-radius: inherit;
  border-bottom-left-radius: inherit;
`;

const RightColumnWrapper = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
  flex: auto;
  overflow: hidden;
  border-radius: inherit;
`;

const RightColumn = styled.div`
  display: flex;
  flex: auto;
  width: 100%;
  overflow-x: auto;
  background-color: ${Color.CoreSlate100};
  align-items: center;
  justify-content: center;
`;

const BUTTON_HALF_WIDTH = 16;
const SLIDE_HALF_WIDTH = 200;
const SLIDE_HALF_SPACING = 32;
const BUTTON_OFFSET = BUTTON_HALF_WIDTH + SLIDE_HALF_SPACING + SLIDE_HALF_WIDTH;
const PreviewContainer = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: center;
  height: 100%;
  width: 100%;
  flex: none;
  overflow: hidden;

  /* Values based on scaling factor to center in frame gutters */
  .slick-prev {
    left: calc(50% - ${BUTTON_OFFSET}px);
  }
  .slick-next {
    right: calc(50% - ${BUTTON_OFFSET}px);
  }
`;

const StyledModalFooter = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-end;
  align-items: center;
  width: 100%;
  height: ${Spacing[9]};
  padding: ${Spacing[4]};
  background-color: ${({ theme }) => theme.color.background.primary};
  border-top: 1px solid ${({ theme }) => theme.color.border.primary};
  border-bottom-left-radius: inherit;
  border-bottom-right-radius: inherit;
`;

const HorizontalContainer = styled.div`
  display: flex;
  align-items: center;
  gap: ${Spacing[3]};
`;

const NextButton = styled(Button).attrs(() => ({ width: 'auto' }))`
  height: ${Spacing[6]};
`;

const MODULE_OPTIONS_LIMITED = [
  ModuleOptionKey.CountrySelectWithRouting,
  ModuleOptionKey.Database,
  ModuleOptionKey.Document,
  ModuleOptionKey.Phone,
];

interface ScreenFlowAddModuleModalProps {
  addStepConfig: AddStepConfig;
  moduleOptions: InquiryTemplateModuleOption[];
  modulePreviewMap: ModulePreviewMap;
  handleLoadModulePreview: (moduleKey: ModuleOptionKey) => Promise<void>;
  handleInsertModule: (
    moduleKey: ModuleOptionKey,
    moduleAddStepConfig: AddStepConfig,
  ) => Promise<void>;
  handleCloseModal: () => void;
  inquiryTemplateTheme?: ThemeAttributes;
  isInsertingModule?: boolean;
}

const ScreenFlowAddModuleModal = ({
  addStepConfig,
  moduleOptions,
  modulePreviewMap,
  handleLoadModulePreview,
  handleInsertModule,
  handleCloseModal,
  inquiryTemplateTheme,
  isInsertingModule,
}: ScreenFlowAddModuleModalProps) => {
  const { nextStep } = addStepConfig;
  const isDocumentHomeModuleEnabled = useFeatureFlag('dashboard:fle-enable-document-home-module');
  const [mainModuleOptions, conditionalModuleOptions] = useMemo(
    () => [
      /**
       * TODO (eric): re-introduce initial-only module options when we have a UX
       * for inserting the initial step. for now, omit these options completely.
       */
      moduleOptions.filter(
        ({ 'is-initial-only': initial, 'is-terminal-only': terminal, key }) =>
          !initial &&
          !terminal &&
          (key !== ModuleOptionKey.DocumentHome || isDocumentHomeModuleEnabled),
      ),

      /**
       * display terminal options in disabled state with tooltip if insertion position is non-terminal.
       * list these after the non-conditional options since we set the first mainModuleOption as selected by default.
       */
      moduleOptions.filter(({ 'is-terminal-only': terminal }) => terminal),
    ],
    [moduleOptions],
  );
  const [selectedModuleOption, setSelectedModuleOption] = useState<
    InquiryTemplateModuleOption | undefined
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  >(mainModuleOptions.length ? mainModuleOptions[0] : null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const [modulePreview, setModulePreview] = useState<ModulePreview | undefined>(null);

  const {
    [Feature.Step]: limitAddStep,
    [Feature.Action]: limitAddAction,
    [Feature.Conditional]: limitAddConditional,
  } = useEnhancedLimitingForAdditional({
    [Feature.Step]: modulePreview?.counts?.steps ?? 0,
    [Feature.Action]: modulePreview?.counts?.actions ?? 0,
    [Feature.Conditional]: modulePreview?.counts?.conditionals ?? 0,
  });

  const handleEnhancedLearnMoreClick = useOnEnhancedLearnMoreClick();
  const hasAccessForTier = useHasAccessForTierDeterminator()(EditorAccessTier.InquiriesBasic);
  const isLimitedModule =
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    !hasAccessForTier && MODULE_OPTIONS_LIMITED.includes(selectedModuleOption.key);

  // initial preview/module load since first module option is set as selected by default
  useEffect(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (modulePreviewMap[selectedModuleOption.key]) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setModulePreview(modulePreviewMap[selectedModuleOption.key]);
    } else {
      // begin pre-loading preview
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      handleLoadModulePreview?.(selectedModuleOption.key);
    }
  }, []);

  // set current module preview post-fetch
  useEffect(() => {
    // Hide preview if loading another preview
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    if (modulePreviewMap[selectedModuleOption.key] == null) {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setModulePreview(null);
    } else {
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      setModulePreview(modulePreviewMap[selectedModuleOption.key]);
    }
  }, [modulePreviewMap, modulePreview, selectedModuleOption]);

  const handleModuleOptionSelected = useCallback(
    (moduleOption: InquiryTemplateModuleOption) => {
      setSelectedModuleOption(moduleOption);
      if (!modulePreviewMap[moduleOption.key]) {
        handleLoadModulePreview?.(moduleOption.key);
      }
    },
    [handleLoadModulePreview, modulePreviewMap, addStepConfig],
  );

  const cardsToShow = useMemo<React.ReactNode[]>(
    () =>
      [...mainModuleOptions, ...conditionalModuleOptions].map((moduleOption) => {
        const {
          'display-name': displayName,
          description,
          'is-terminal-only': terminalOnly,
          key,
        } = moduleOption;
        const disableTerminal = terminalOnly && nextStep != null;
        const moduleCard = (
          <TemplateModuleCard
            size='sm'
            justify='center'
            id={`${key}-module-card`}
            key={`${key}-module-card`}
            title={displayName}
            description={description}
            icon={<GridIcon size={24} />}
            onClick={() => handleModuleOptionSelected(moduleOption)}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            selected={selectedModuleOption.key === key}
            disabled={disableTerminal}
            // You can access this modal only if the version is editable, so we don't have to worry
            // about accounting for non-editable version.
            isLimited={!hasAccessForTier && MODULE_OPTIONS_LIMITED.includes(moduleOption.key)}
            withBorder
          />
        );
        return disableTerminal ? (
          <Tooltip
            side='top'
            key={`${key}-module-card-tooltip`}
            trigger={<span>{moduleCard}</span>}
            portal={false}
          >
            <Paragraph>{terminalModuleHelpText}</Paragraph>
          </Tooltip>
        ) : (
          moduleCard
        );
      }),
    [
      conditionalModuleOptions,
      handleModuleOptionSelected,
      hasAccessForTier,
      mainModuleOptions,
      nextStep,
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      selectedModuleOption.key,
    ],
  );

  return (
    <StyledModalContainer>
      <ScreenFlowAddModuleModalHeader handleCloseModal={handleCloseModal} />
      <ContentWrapper>
        <LeftColumn>{cardsToShow}</LeftColumn>
        <RightColumnWrapper>
          <RightColumn>
            <PreviewContainer>
              <InquiryTemplatePreview
                clientData={(modulePreview?.steps ?? []).map((step) => step['client-data'])}
                theme={inquiryTemplateTheme}
                scaleRatio={0.7}
              />
            </PreviewContainer>
          </RightColumn>
          <StyledModalFooter data-test='modules-modal__footer'>
            <HorizontalContainer>
              {isLimitedModule && (
                <ProductUpsellTooltip
                  side='top'
                  accessTier={EditorAccessTier.InquiriesBasic}
                  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                  learnMoreId={`${selectedModuleOption.key}-module-card`}
                  // Need a z-index higher than that of modal
                  zIndex={Z_INDICES.toast}
                  trigger={
                    <div>
                      <InquiriesGatingSticker
                        /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                        dataMarketingIdSuffix={`${selectedModuleOption.key}-module-card`}
                        stickerType={EnhancedGatingStickerType.Limited}
                      />
                    </div>
                  }
                />
              )}
              <LearnMoreTooltip
                id='add-module-modal__learn-more-tooltip'
                tooltipContent={`Inserting this module would exceed the number ${compact([
                  limitAddStep ? `maximum steps (${MAX_STEPS_FOR_NON_ENHANCED})` : null,
                  limitAddAction ? `maximum actions (${MAX_ACTIONS_FOR_NON_ENHANCED})` : null,
                  limitAddConditional
                    ? `maximum conditionals (${MAX_CONDITIONAL_STEP_FOR_NON_ENHANCED})`
                    : null,
                ]).join(
                  ' and ',
                )} allowed under your current plan. To learn more about other plans, you can send a message to a Persona representative.`}
                onClick={() =>
                  handleEnhancedLearnMoreClick({
                    feature: Feature.Module,
                  })
                }
                disabled={!limitAddStep && !limitAddAction && !limitAddConditional}
                side='top'
                trigger={
                  <NextButton
                    id='modules-modal__next-button'
                    isPending={isInsertingModule}
                    disabled={
                      selectedModuleOption == null ||
                      modulePreview == null ||
                      limitAddStep ||
                      limitAddAction ||
                      isLimitedModule ||
                      limitAddConditional
                    }
                    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
                    onClick={() => handleInsertModule(selectedModuleOption.key, addStepConfig)}
                  >
                    Insert
                  </NextButton>
                }
                portal={false}
              />
            </HorizontalContainer>
          </StyledModalFooter>
        </RightColumnWrapper>
      </ContentWrapper>
    </StyledModalContainer>
  );
};

interface ScreenFlowAddModuleModalHeaderProps {
  handleCloseModal: () => void;
}

const ScreenFlowAddModuleModalHeader = ({
  handleCloseModal,
}: ScreenFlowAddModuleModalHeaderProps) => {
  return (
    <StyledModalHeader>
      <HeaderTitleWrapper>
        <Heading size='sm'>Choose a module</Heading>
        <CloseButton
          id='modules-modal__close'
          data-test='modules-modal__close'
          onClick={handleCloseModal}
        >
          <XIcon size={24} />
        </CloseButton>
      </HeaderTitleWrapper>
      <SearchFilterRow>{/* omit until more module options are added */}</SearchFilterRow>
    </StyledModalHeader>
  );
};

export default ScreenFlowAddModuleModal;
